import { API_URL } from "@/shared/utils/API_URL";
import { buildQuery } from "@/shared/utils/buildQuery";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import Cookies from 'js-cookie';
import { useEffect, useMemo, useState } from "react";
import useEntities from "./createOrder/useEntities";
import useDateFilter from "./useDateFilter";
import useQueries from "./useQueries";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useDebounce } from "./useDebounce";

const fetchData = async (sessionId: string | undefined, query: string, licenceId: string | null) => {
    const response = await axios.post(
        API_URL,
        { q: query },
        {
            headers: { 'Content-Type': 'application/json' },
            params: { t: 'sel', phone: '', id: licenceId, s: sessionId, btn: 'w_itm', api: 'itqan' },
        }
    );
    return response.data;
};


const escapeSqlValue = (value: string) => value.replace(/'/g, "''");
const sanitizeSearchInput = (value: string) =>
    escapeSqlValue(value)
        .replace(/;/g, '')
        .replace(/--/g, '')
        .replace(/\/\*/g, '')
        .replace(/\*\//g, '');

const PAGE_SIZE = 20;

export default function useItemSalesReport() {
    const { t } = useTranslation();

    const sessionId = Cookies.get('sessionId');
    const licenceId = localStorage.getItem("licence_id");

    const { date, setDate, handleQuickSelect } = useDateFilter();


    const queries = useQueries();
    const { projectsWithBusiness, colors, users } = useEntities();


    const [selectedProject, setSelectedProject] = useState('');
    const [selectedColor, setSelectedColor] = useState('');
    const [selectedSize, setSelectedSize] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [zeroDeliveryOnly, setZeroDeliveryOnly] = useState(false);
    const [discountedOnly, setDiscountedOnly] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(0);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');


    const debouncedSearchValue = useDebounce(searchValue, 1000);


    const filters = useMemo(() => {
        const conditions: string[] = [];
        if (date?.from && date?.to) {
            conditions.push(`AND o.orderdate BETWEEN '${date.from} 00:00:00' AND '${date.to} 23:59:59'`);
        }

        if (selectedProject) {
            conditions.push(`AND CurrId = '${escapeSqlValue(selectedProject)}'`)
        }
        if (selectedColor) {
            conditions.push(`AND SUBSTRING_INDEX(od.color, '█', -1) = '${escapeSqlValue(selectedColor)}'`);
        }

        if (selectedSize) {
            conditions.push(`AND od.size = '${escapeSqlValue(selectedSize)}'`);
        }

        if (selectedEmployee) {
            conditions.push(`AND od.theuserid = '${escapeSqlValue(selectedEmployee)}'`);
        }

        if (zeroDeliveryOnly) {
            conditions.push(`AND SUBSTRING_INDEX(a.custom1, ',', -1) = '0'`);
        }
        if (discountedOnly) {
            conditions.push(`AND od.price < COALESCE(ip.buyprice, 0)`);
        }
        if (debouncedSearchValue) {
            const s = sanitizeSearchInput(debouncedSearchValue);
            conditions.push(`AND (i.itemstring LIKE '%${s}%' OR u.userstring LIKE '%${s}%')`);
        }

        return conditions.join(' ');

    }, [date, selectedProject, selectedColor, selectedSize, selectedEmployee, zeroDeliveryOnly, discountedOnly, debouncedSearchValue]);


    const canQuery = !!queries;

    const itemSalesReportQuery = canQuery
        ? buildQuery(queries!.itemSalesReportQuery, {
            filters,
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
            sort_order: sortOrder === 'asc' ? 'ASC' : 'DESC',
        })
        : '';

    const itemSalesReportCountQuery = canQuery
        ? buildQuery(queries!.itemSalesReportCountQuery, {
            filters,
        })
        : '';



    const { data: itemSalesReport, isLoading, isFetching } = useQuery({
        queryKey: ['itemSalesReport', itemSalesReportQuery, sessionId, licenceId, sortOrder],
        queryFn: () => fetchData(sessionId, itemSalesReportQuery, licenceId),
        enabled: !!itemSalesReportQuery && !!sessionId && !!licenceId,
        staleTime: 0,
        refetchOnMount: true,
        placeholderData: (prev: any) => prev,
    });

    const { data: itemSalesReportCount, isLoading: isCountLoading, isFetching: isCountFetching } = useQuery({
        queryKey: ['itemSalesReportCount', itemSalesReportCountQuery, sessionId, licenceId],
        queryFn: () => fetchData(sessionId, itemSalesReportCountQuery, licenceId),
        enabled: !!itemSalesReportCountQuery && !!sessionId && !!licenceId,
        staleTime: 0,
        refetchOnMount: true,
        placeholderData: (prev: any) => prev,
    });


    useEffect(() => {
        setPage(0);
    }, [date, debouncedSearchValue, selectedProject, selectedColor,
        selectedSize, selectedEmployee, zeroDeliveryOnly, discountedOnly, sortOrder]);

    const rows: any[] = itemSalesReport?.data?.result || itemSalesReport || [];
    const totalRows: number = itemSalesReportCount?.data?.result?.[0]?.total
        ?? itemSalesReportCount?.[0]?.total
        ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));


    const handleExport = (format: 'pdf' | 'excel', customTitle = 'تقرير مبيعات الأصناف') => {
        if (!itemSalesReportQuery) {
            toast.error(t("no_query_available"));
            return;
        }

        const cleanQuery = itemSalesReportQuery
            .replace(/\s+LIMIT\s+\d+/gi, '')
            .replace(/\s+OFFSET\s+\d+/gi, '');

        const exportColumns = [
            { key: 'item_name', label: t('item_name') },
            { key: 'item_color', label: t('color') },
            { key: 'item_size', label: t('size') },
            { key: 'project_id', label: t('store') },
            { key: 'user_name', label: t('employee') },
            { key: 'order_date', label: t('order_date') },
            { key: 'original_price', label: t('original_price') },
            { key: 'sell_price', label: t('sell_price') },
            { key: 'delivery_fees', label: t('delivery_fees') },
        ];
        const exportUrl = new URL("https://w-ord.ntelgnt.com/export.php");

        exportUrl.searchParams.append("format", format);
        exportUrl.searchParams.append("title", customTitle);
        exportUrl.searchParams.append("id", licenceId || "");
        exportUrl.searchParams.append("s", sessionId || "");

        const form = document.createElement("form");
        form.method = "POST";
        form.action = exportUrl.toString();
        form.target = "_blank";

        const qInput = document.createElement("input");
        qInput.type = "hidden";
        qInput.name = "q";
        qInput.value = cleanQuery;
        form.appendChild(qInput);

        const columnsInput = document.createElement("input");
        columnsInput.type = "hidden";
        columnsInput.name = "columns";
        columnsInput.value = JSON.stringify(exportColumns);
        form.appendChild(columnsInput);

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    };

    const isSearchPending = searchValue !== debouncedSearchValue;
    const isSearching = isSearchPending || isFetching;

    return {
        date, setDate, handleQuickSelect,
        selectedProject, setSelectedProject, projectsWithBusiness,
        selectedColor, setSelectedColor, colors,
        selectedSize, setSelectedSize,
        selectedEmployee, setSelectedEmployee, users,
        zeroDeliveryOnly, setZeroDeliveryOnly,
        discountedOnly, setDiscountedOnly,
        searchValue, setSearchValue,
        page, setPage, totalPages, totalRows, pageSize: PAGE_SIZE,
        rows,
        isLoading: isLoading || isCountLoading,
        isFetching: isFetching || isCountFetching,
        handleExport, isSearching, sortOrder, setSortOrder,

    };

}